import axios, { AxiosRequestConfig } from "axios";
import IResponse from "../interfaces/IResponse";
import { IProforma } from "../interfaces/IProforma";
import ApiHelper from "./ApiHelper";
import { baseURL } from "../config";

const BASE_URL = `${baseURL}api`

const config: AxiosRequestConfig = {
    headers: ApiHelper.createHeader(),
}

const configForFiles: AxiosRequestConfig = {
    headers: ApiHelper.createHeaderForFiles(),
}

interface ICreateInvoiceDto {
    number: number;
    date: string;
    createdBy: string;
    clientName: string;
    positions: Array<{
        name: string;
        description?: string;
        quantity: number;
        grossPrice: number;
        vat: number;
        producer: string;
    }>;
    paymentTerm: number;
    advanceAmmount: number;
    clientNIP?: string;
    clientAddress?: Array<{
        street?: string;
        houseNumber?: string;
        apartmentNumber?: string;
        postalCode?: string;
        city?: string;
    }>;
    clientEmail?: string;
    clientPhone?: string;
}

interface ICreateInvoice extends IResponse {
    newInvoice: any | null;
}

const createInvoice = async (invoice: ICreateInvoiceDto): Promise<ICreateInvoice> => {
    const url = `${BASE_URL}/createProForma`;

    try {
        const res = await axios.post(url, invoice, config);

        const result = {
            ok: true,
            message: 'Dodano fakturę proforma',
            status: res.status,
            newInvoice: res.data.invoice,
        };

        return result;
    } catch (error) {
        let message = 'Nie dodano faktury proforma';
        let status = 400;

        if (axios.isAxiosError(error) && error.response) {
            if (error.response.status === 403) message = 'Nie możesz tego zrobić';
            if (error.response.status) status = error.response.status;
        }

        return {
            ok: false,
            status,
            message,
            newInvoice: null,
        };
    }
};

interface IGetNextInvoiceResponse extends IResponse {
    nextNumber: number | null;
    year: number;
}

const getNextInvoiceNumber = async (year: number): Promise<IGetNextInvoiceResponse> => {
    
    const url = `${BASE_URL}/getNextInvoiceNumber/${year}`;
    try {
        const res = await axios.get(url, config);
        
        return {
            status: res.status,
            message: 'Pobrano kolejny numer faktury',
            nextNumber: res.data.nextNumber,
            year: res.data.year,
            ok: true,
        };
    } catch (error) {
        return {
            status: 500,
            nextNumber: null,
            year: new Date().getFullYear(),
            ok: false,
            message: "Nie udało się pobrać kolejnego numeru faktury",
        };
    }
};

const sendInvoicePDF = async (pdfFile: any, emails: string[]): Promise<IResponse> => {
    const url = `${BASE_URL}/sendInvoicePDF`;
    const formData = new FormData();
    formData.append('file', pdfFile, pdfFile instanceof File ? pdfFile.name : 'faktura.pdf');
    formData.append('emails', JSON.stringify(emails));

    try {
        const res = await axios.post(url, formData, configForFiles);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || 'Faktura została wysłana na wskazane adresy email',
        };
    } catch (error) {
        let message = 'Nie udało się wysłać faktury';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
        };
    }
};


interface IGetInvoicesDataResponse extends IResponse {
    invoiceData: IProforma[] | null;
    page?: number;
    limit?: number;
    totalPages?: number;
    totalCount?: number;
}

const getInvoicesData = async ({
    page = 1,
    limit = 20,
    statusValue,
}: { page?: number; limit?: number; statusValue?: string } = {}): Promise<IGetInvoicesDataResponse> => {
    let url = `${BASE_URL}/getAllInvoices?page=${page}&limit=${limit}`;
    if (statusValue && statusValue !== "all") {
        url += `&status=${encodeURIComponent(statusValue)}`;
    }
    try {
        const res = await axios.get(url, config);
        return {
            ok: true,
            status: res.status,
            message: 'Pobrano dane faktury',
            invoiceData: res.data.invoices,
            page: res.data.page,
            limit: res.data.limit,
            totalPages: res.data.totalPages,
            totalCount: res.data.totalCount,
        };
    } catch (error) {
        let message = 'Nie udało się pobrać danych faktury';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoiceData: null,
            page,
            limit,
            totalPages: 1,
            totalCount: 0,
        };
    }
};


interface IGetInvoiceByIdResponse extends IResponse {
    invoice: IProforma | null;
}

const getInvoiceById = async (invoiceId: string): Promise<IGetInvoiceByIdResponse> => {
    const url = `${BASE_URL}/getInvoiceById/${invoiceId}`;
    try {
        const res = await axios.get(url, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Pobrano fakturę proforma",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się pobrać faktury proforma';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
};


interface IEditProformaStatusDto {
    _id: string;
    status: IProforma["status"];
}

interface IEditProformaStatusResponse extends IResponse {
    invoice: IProforma | null;
}

const editProformaStatus = async (data: IEditProformaStatusDto): Promise<IEditProformaStatusResponse> => {
    const url = `${BASE_URL}/editProforma`;
    try {
        const res = await axios.patch(url, data, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Status faktury został zaktualizowany",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się zaktualizować statusu faktury';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
};


interface IEditProformaZkDto {
    _id: string;
    zk: string;
}

interface IEditProformaZkResponse extends IResponse {
    invoice: IProforma | null;
}

const editProformaZk = async (data: IEditProformaZkDto): Promise<IEditProformaZkResponse> => {
    const url = `${BASE_URL}/editProforma`;
    try {
        const res = await axios.patch(url, data, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Zaktualizowano ZK faktury",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się zaktualizować ZK faktury';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
};


interface IEditProformaZdDto {
    _id: string;
    zd: string;
}

interface IEditProformaZdResponse extends IResponse {
    invoice: IProforma | null;
}

const editProformaZd = async (data: IEditProformaZdDto): Promise<IEditProformaZdResponse> => {
    const url = `${BASE_URL}/editProforma`;
    try {
        const res = await axios.patch(url, data, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Zaktualizowano ZD faktury",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się zaktualizować ZD faktury';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
};


interface IEditProformaDto {
    _id: string;
    number: number;
    date: string;
    createdBy: string;
    clientName: string;
    positions: Array<{
        name: string;
        description?: string;
        quantity: number;
        grossPrice: number;
        vat: number;
        producer: string;
    }>;
    paymentTerm: number;
    advanceAmmount: number;
    clientNIP?: string;
    clientAddress?: Array<{
        street?: string;
        houseNumber?: string;
        apartmentNumber?: string;
        postalCode?: string;
        city?: string;
    }>;
    clientEmail?: string;
    clientPhone?: string;
    zk?: string;
    zd?: string;
    status?: string;
}

interface IEditProformaResponse extends IResponse {
    invoice: IProforma | null;
}

const editProforma = async (data: IEditProformaDto): Promise<IEditProformaResponse> => {
    const url = `${BASE_URL}/editProforma`;
    try {
        const res = await axios.put(url, data, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Faktura proforma została zaktualizowana",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się zaktualizować faktury proforma';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
}


interface IEditProformaPositionZdDto {
    invoiceId: string;
    positionId: string;
    zd: string;
}

interface IEditProformaPositionZdResponse extends IResponse {
    invoice: IProforma | null;
}

const editProformaPositionZd = async (
    data: IEditProformaPositionZdDto
): Promise<IEditProformaPositionZdResponse> => {
    const url = `${BASE_URL}/editProFormaPositionZd`;

    try {
        const res = await axios.patch(url, data, config);
        return {
            ok: true,
            status: res.status,
            message: res.data.message || "Zaktualizowano ZD pozycji",
            invoice: res.data.invoice,
        };
    } catch (error) {
        let message = 'Nie udało się zaktualizować ZD pozycji';
        let status = 400;
        if (axios.isAxiosError(error) && error.response) {
            message = error.response.data?.message || message;
            status = error.response.status;
        }
        return {
            ok: false,
            status,
            message,
            invoice: null,
        };
    }
};


const InvoicesController = {
    createInvoice,
    getNextInvoiceNumber,
    editProformaPositionZd,
    sendInvoicePDF,
    getInvoicesData,
    getInvoiceById,
    editProformaStatus,
    editProformaZk,
    editProformaZd,
    editProforma,
};

export default InvoicesController;